import { useState, useRef, useCallback } from 'react';
import { Wine, StagedWine, Message } from '@/types';
import { buildSystemPrompt, CONFIG } from '@/constants';
import { callGeminiProxy } from '@/utils/geminiProxy';
import { captureError } from '@/utils/captureError';
import { remyErrorMessage, EMPTY_RESPONSE } from '@/utils/remyErrorMessage';
import { detectCellarIntent, isAffirmative } from './remyIntentDetection';

const MAX_TOOL_ROUNDS = 5;

// functions/src/gemini.ts rejects a contents array longer than 50. The trim
// below runs once per turn, before the tool loop appends two entries per
// round, so the post-trim ceiling has to leave room for the worst case.
const SERVER_MAX_CONTENTS = 50;
const MAX_HISTORY_ENTRIES = SERVER_MAX_CONTENTS - MAX_TOOL_ROUNDS * 2;

interface UseRemyChatDeps {
  handleToolCalls: (calls: any[]) => Promise<{ result: string }[]>;
  stagedWineRef: React.MutableRefObject<StagedWine | null>;
}

export function useRemyChat(
  cellarSnapshot: string,
  // Currently unread, but useGeminiLive passes it positionally and that file
  // must not be modified — keep the parameter so the call signature holds.
  localCellar: Wine[],
  deps: UseRemyChatDeps,
) {
  const [transcript, setTranscript] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatMode, setChatMode] = useState<'general' | 'cellar'>('general');
  const hasOfferedCellarBridge = useRef(false);
  const awaitingCellarConfirmation = useRef(false);
  const historyRef = useRef<any[]>([]);
  const inFlightRef = useRef(false);

  const sendMessage = useCallback(async (text: string, imageBase64?: string, isVoice: boolean = false) => {
    if (!text && !imageBase64) return;

    // Sends must be serialised. Two concurrent sends share historyRef, so a
    // failing send's rollback would restore a snapshot taken before the other
    // send's turn and silently destroy it. A ref, not state — setIsProcessing
    // lands too late to gate a second call in the same tick.
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setIsProcessing(true);

    // ── Mode detection ──
    // Images always trigger cellar mode (label scan → stageWine flow needs inventory)
    let effectiveMode: 'general' | 'cellar' = chatMode;
    if (imageBase64) {
      effectiveMode = 'cellar';
      setChatMode('cellar');
    } else if (chatMode === 'general') {
      if (awaitingCellarConfirmation.current && isAffirmative(text)) {
        effectiveMode = 'cellar';
        setChatMode('cellar');
        awaitingCellarConfirmation.current = false;
      } else if (detectCellarIntent(text)) {
        effectiveMode = 'cellar';
        setChatMode('cellar');
      }
    }

    let localPrice: number | null = null;
    let localQty: number | null = null;
    const priceMatch = text.match(/\$(\d+(\.\d{2})?)/);
    const qtyMatch = text.match(/(\d+)\s*bottles?/i);
    if (priceMatch) localPrice = parseFloat(priceMatch[1]);
    if (qtyMatch) localQty = parseInt(qtyMatch[1]);

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
      image: imageBase64
    };
    setTranscript(prev => [...prev, userMessage]);

    // Restore point: if the send fails, the user turn below must not stay in
    // history — otherwise the next message stacks a second consecutive user
    // turn with no model reply between them and degrades the context.
    const historySnapshot = [...historyRef.current];

    try {
      const parts: any[] = [{ text: text || "Analyze this label." }];
      if (imageBase64) parts.push({ inlineData: { data: imageBase64, mimeType: 'image/jpeg' } });
      if (localPrice || localQty) {
        parts.push({ text: `[System Note: User provided potential values - Price: ${localPrice}, Qty: ${localQty}]` });
      }

      historyRef.current.push({ role: 'user', parts });

      // Enforce history turn limit to prevent unbounded context growth.
      const maxTurns = CONFIG.MAX_HISTORY_TURNS;
      const userIndices: number[] = [];
      historyRef.current.forEach((entry, i) => {
        if (entry.role === 'user') userIndices.push(i);
      });
      if (userIndices.length > maxTurns) {
        const cutIndex = userIndices[userIndices.length - maxTurns];
        historyRef.current = historyRef.current.slice(cutIndex);
      }

      // ...and then by total entries, which is what the server actually
      // limits. Counting user turns alone undercounts badly: a tool-using
      // turn also appends a model functionCall and a function response per
      // round, so 15 tool-heavy turns is well over 50 entries and the proxy
      // starts rejecting the whole conversation with a 400 mid-session.
      // Always cut at a user entry — slicing into a turn would orphan a
      // functionResponse from its functionCall.
      while (historyRef.current.length > MAX_HISTORY_ENTRIES) {
        const nextTurn = historyRef.current.findIndex(
          (entry, i) => i > 0 && entry.role === 'user',
        );
        if (nextTurn === -1) break;
        historyRef.current = historyRef.current.slice(nextTurn);
      }

      // Build prompt with mode-aware inventory injection
      const includeBridge = effectiveMode === 'general' && !hasOfferedCellarBridge.current;
      const prompt = buildSystemPrompt(
        effectiveMode === 'cellar' ? cellarSnapshot : null,
        JSON.stringify(deps.stagedWineRef.current),
        includeBridge
      );

      const toolDeclarations = [{ functionDeclarations: [
        { name: 'queryInventory', description: "Search the user's wine cellar. Use this whenever you need to find specific wines, answer questions about inventory, make food pairing recommendations, or check what's available. Always use this tool rather than relying on the cellar summary for specific wine queries.", parameters: {
          type: "OBJECT",
          properties: {
            wineType: { type: "STRING", description: "Wine type filter: Red, White, Rosé, Sparkling, Dessert, Fortified" },
            country: { type: "STRING", description: "Country filter" },
            region: { type: "STRING", description: "Region filter" },
            producer: { type: "STRING", description: "Producer name filter (partial match)" },
            grapeVarieties: { type: "ARRAY", items: { type: "STRING" }, description: "Grape variety filter" },
            vintageMin: { type: "NUMBER", description: "Minimum vintage year" },
            vintageMax: { type: "NUMBER", description: "Maximum vintage year" },
            priceMin: { type: "NUMBER", description: "Minimum price" },
            priceMax: { type: "NUMBER", description: "Maximum price" },
            maturityStatus: { type: "STRING", description: "Maturity filter: HOLD (Ripening), DRINK_OR_HOLD (Hold or Sip), DRINK (Peak), DRINK_SOON (Fading), or PAST_PEAK (Tired)" },
            query: { type: "STRING", description: "Free text search across producer, name, grape varieties, region, appellation" },
            sortBy: { type: "STRING", description: "Sort field: vintage, price, or rating" },
            sortOrder: { type: "STRING", description: "Sort direction: asc or desc" },
            limit: { type: "NUMBER", description: "Max results to return (default 10, max 20)" },
            semanticQuery: { type: "STRING", description: "Natural language description of what you're looking for. Use for food pairing queries, mood-based requests, or characteristic descriptions. Examples: 'bold earthy red for braised meat', 'crisp refreshing white for seafood'. Can be combined with structured filters." },
          },
        } },
        { name: 'stageWine', parameters: {
          type: "OBJECT",
          properties: {
            producer: { type: "STRING", description: "Wine producer/house name" },
            vintage: { type: "NUMBER", description: "Vintage year" },
            type: { type: "STRING", description: "Wine type: Red, White, Rosé, Sparkling, Dessert, Fortified" },
            name: { type: "STRING", description: "Cuvee/bottling name only — must NOT duplicate producer or grape variety" },
            grapeVarieties: { type: "ARRAY", items: { type: "OBJECT", properties: { name: { type: "STRING", description: "Variety name, e.g. Shiraz" }, pct: { type: "NUMBER", description: "Percentage (optional). Integer 1-100." } }, required: ["name"] }, description: "Grape varieties, ordered by dominance. Max 5." },
            region: { type: "STRING", description: "Wine region" },
            country: { type: "STRING", description: "Country of origin" },
            appellation: { type: "STRING", description: "Appellation or classification" },
            tastingNotes: { type: "STRING", description: "Comma-separated flavour adjectives (5-8 descriptors)" },
            drinkFrom: { type: "NUMBER", description: "Suggested drink-from year" },
            drinkUntil: { type: "NUMBER", description: "Suggested drink-until year" },
            format: { type: "STRING", description: "Bottle format e.g. 750ml, 1.5L" },
          },
          required: ['producer', 'vintage', 'type'],
        } },
        { name: 'commitWine', parameters: { type: "OBJECT", properties: { price: { type: "NUMBER" }, quantity: { type: "NUMBER" } }, required: ['price'] } }
      ] }];

      // Multi-round tool call loop
      let response = await callGeminiProxy({
        model: CONFIG.MODELS.TEXT,
        contents: historyRef.current,
        systemInstruction: prompt,
        tools: toolDeclarations,
      });

      let finalContent = response.text || "";
      let toolRound = 0;

      while (response.functionCalls?.length > 0 && response.candidateContent && toolRound < MAX_TOOL_ROUNDS) {
        toolRound++;
        const calls = response.functionCalls;
        const toolResults = await deps.handleToolCalls(calls);
        historyRef.current.push(response.candidateContent);
        historyRef.current.push({
          role: 'function',
          parts: calls.map((call: any, i: number) => ({
            functionResponse: {
              name: call.name,
              response: toolResults![i],
            },
          })),
        });

        response = await callGeminiProxy({
          model: CONFIG.MODELS.TEXT,
          contents: historyRef.current,
          systemInstruction: prompt,
          tools: toolDeclarations,
        });
        finalContent = response.text || finalContent || "Processed.";
      }

      // The proxy returns HTTP 200 with text:null on a safety block or a
      // MAX_TOKENS finish. Persisting that would push an empty model turn into
      // history and render a blank bubble — indistinguishable from no reply.
      if (!finalContent.trim()) throw new Error(EMPTY_RESPONSE);

      historyRef.current.push({ role: 'model', parts: [{ text: finalContent }] });
      setTranscript(prev => [...prev, { id: Date.now().toString(), role: 'assistant', content: finalContent, timestamp: new Date() }]);

      // Track bridge offer — after first general response, mark as offered
      if (effectiveMode === 'general' && !hasOfferedCellarBridge.current) {
        hasOfferedCellarBridge.current = true;
        awaitingCellarConfirmation.current = true;
      }

    } catch (e) {
      captureError(e, { context: 'remy_chat', mode: effectiveMode, hasImage: !!imageBase64 });
      historyRef.current = historySnapshot;
      setTranscript(prev => [...prev, {
        id: `${Date.now()}-error`,
        role: 'assistant',
        content: remyErrorMessage(e),
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      inFlightRef.current = false;
      setIsProcessing(false);
    }
  }, [cellarSnapshot, chatMode, deps.handleToolCalls]);

  return { transcript, isProcessing, sendMessage };
}
